import { marked } from "marked";
import DOMPurify from "dompurify";
import TurndownService from "turndown";
// @ts-expect-error - the GFM plugin ships without types
import { gfm } from "turndown-plugin-gfm";

/**
 * Footnotes: `text[^1]` in the body and `[^1]: the note` on its own line. Numbered in the order they are first
 * used; a note that is never used is left out and a reference without a note stays as typed. Text inside
 * code fences and `inline code` is not touched. Returns the Markdown without the note lines, and the notes.
 */
export function extractFootnotes(md: string): { body: string; notes: { id: string; n: number; text: string }[] } {
  const lines = md.split(/\r?\n/);
  const defs = new Map<string, string>();
  const kept: string[] = [];
  let fence = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*(```|~~~)/.test(line)) { fence = !fence; kept.push(line); continue; }
    const m = !fence && line.match(/^\[\^([^\]\s]+)\]:\s*(.*)$/);
    if (!m) { kept.push(line); continue; }
    let text = m[2];
    while (i + 1 < lines.length && /^( {2,}|\t)\S/.test(lines[i + 1])) text += " " + lines[++i].trim();
    defs.set(m[1], text);
  }
  const order: string[] = [];
  fence = false;
  const body = kept.map((line) => {
    if (/^\s*(```|~~~)/.test(line)) { fence = !fence; return line; }
    if (fence) return line;
    return line.split(/(`+[^`]*`+)/).map((part, k) => {
      if (k % 2 === 1) return part; // inline code
      return part.replace(/\[\^([^\]\s]+)\]/g, (all, id: string) => {
        if (!defs.has(id)) return all;
        if (!order.includes(id)) order.push(id);
        const n = order.indexOf(id) + 1;
        return `<sup><a href="#fn-${id}" id="fnref-${id}">${n}</a></sup>`;
      });
    }).join("");
  }).join("\n");
  return { body, notes: order.map((id, i) => ({ id, n: i + 1, text: defs.get(id)! })) };
}

/** Markdown -> HTML (CommonMark + GitHub tables, task lists, strikethrough, bare-link autolinks and footnotes).
 * The result is sanitised, so `javascript:` links, inline scripts and event handlers are removed. */
export function markdownToHtml(md: string): string {
  const { body, notes } = extractFootnotes(md);
  let raw = marked.parse(body, { gfm: true, breaks: false, async: false }) as string;
  if (notes.length) {
    raw += `\n<section class="footnotes"><hr><ol>\n${notes.map((f) => `<li id="fn-${f.id}">${marked.parseInline(f.text, { gfm: true, async: false }) as string} <a href="#fnref-${f.id}">\u21a9</a></li>`).join("\n")}\n</ol></section>`;
  }
  return DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } }).trim();
}

let td: TurndownService | null = null;
function turndown(): TurndownService {
  if (!td) {
    td = new TurndownService({ headingStyle: "atx", codeBlockStyle: "fenced", bulletListMarker: "-", emDelimiter: "*", hr: "---" });
    td.use(gfm);
  }
  return td;
}

/** HTML -> Markdown with nested lists, numbered lists, tables, code blocks and quotes. The HTML is sanitised first. */
export function htmlToMarkdown(html: string): string {
  const clean = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  return turndown().turndown(clean).trim();
}
